import { MessageChannel, MessageDelayUnit } from "@prisma/client";
import { ComponentType } from "react";
import IconChatBubble from "@/app/(components)/generics/Icons/IconChatBubble";
import IconClock from "@/app/(components)/generics/Icons/IconClock";
import IconFlag from "@/app/(components)/generics/Icons/IconFlag";
import IconLinkedIn from "@/app/(components)/generics/Icons/IconLinkedIn";
import IconMailLink from "@/app/(components)/generics/Icons/IconMailLink";
import IconSend from "@/app/(components)/generics/Icons/IconSend";
import { SuggestionOption } from "@/app/(components)/generics/SuggestionSelect/SuggestionSelect";
import { TFn } from "@/app/translations/TFn";
import { TKey } from "@/app/translations/TKey";
import {
  MessageSequenceStepData,
  MessageSequenceStepVariantData,
} from "@/app/types/messages-content/MessageSequenceData";
import { StandardSequenceStep } from "@/app/types/messages-content/StandardSequenceStep";
import { UMessageDelay } from "@/app/utils/UMessageDelay";
import { Limits } from "@/app/vars/Limits";

export const CHANNEL_LABEL_KEYS: Record<MessageChannel, TKey> = {
  [MessageChannel.LINKEDIN]: TKey.MESSAGES_CONTENT_CHANNEL_LINKEDIN,
  [MessageChannel.EMAIL]: TKey.MESSAGES_CONTENT_CHANNEL_EMAIL,
  [MessageChannel.WHATSAPP]: TKey.MESSAGES_CONTENT_CHANNEL_WHATSAPP,
};

export const CHANNEL_ICONS: Record<MessageChannel, ComponentType<{ size?: number }>> = {
  [MessageChannel.LINKEDIN]: IconLinkedIn,
  [MessageChannel.EMAIL]: IconMailLink,
  [MessageChannel.WHATSAPP]: IconChatBubble,
};

export const DELAY_LABEL_KEYS: Record<MessageDelayUnit, TKey> = {
  [MessageDelayUnit.DAY]: TKey.MESSAGES_CONTENT_DELAY_DAY,
  [MessageDelayUnit.HOUR]: TKey.MESSAGES_CONTENT_DELAY_HOUR,
  [MessageDelayUnit.MINUTE]: TKey.MESSAGES_CONTENT_DELAY_MINUTE,
};

// Widths measured on the messages the model actually produces: a short address line, a
// body that fills its lines, and a closing ask. Each step is shorter than the one before,
// which is what the skill asks of the real copy.
export const TEMPLATE_SKELETON_LINES: Record<StandardSequenceStep, string[]> = {
  [StandardSequenceStep.OPENING]: ["18%", "100%", "39%"],
  [StandardSequenceStep.FOLLOW_UP_1]: ["24%", "87%"],
  [StandardSequenceStep.FOLLOW_UP_2]: ["20%", "72%"],
  [StandardSequenceStep.FOLLOW_UP_3]: ["22%", "58%"],
  [StandardSequenceStep.BREAK_UP]: ["42%", "18%"],
};

export function getTemplateSkeleton(type: string): string[] {
  return (
    TEMPLATE_SKELETON_LINES[type as StandardSequenceStep] ??
    TEMPLATE_SKELETON_LINES[StandardSequenceStep.FOLLOW_UP_1]
  );
}

export const STEP_ICONS: Record<StandardSequenceStep, ComponentType<{ size?: number }>> = {
  [StandardSequenceStep.OPENING]: IconSend,
  [StandardSequenceStep.FOLLOW_UP_1]: IconClock,
  [StandardSequenceStep.FOLLOW_UP_2]: IconClock,
  [StandardSequenceStep.FOLLOW_UP_3]: IconClock,
  [StandardSequenceStep.BREAK_UP]: IconFlag,
};

export const STEP_LABEL_KEYS: Record<StandardSequenceStep, TKey> = {
  [StandardSequenceStep.OPENING]: TKey.MESSAGES_CONTENT_STEP_OPENING,
  [StandardSequenceStep.FOLLOW_UP_1]: TKey.MESSAGES_CONTENT_STEP_FOLLOW_UP_1,
  [StandardSequenceStep.FOLLOW_UP_2]: TKey.MESSAGES_CONTENT_STEP_FOLLOW_UP_2,
  [StandardSequenceStep.FOLLOW_UP_3]: TKey.MESSAGES_CONTENT_STEP_FOLLOW_UP_3,
  [StandardSequenceStep.BREAK_UP]: TKey.MESSAGES_CONTENT_STEP_BREAK_UP,
};

export function isStandardStep(type: string): type is StandardSequenceStep {
  return Object.values(StandardSequenceStep).includes(type as StandardSequenceStep);
}

export function getStepLabel(type: string, t: TFn): string {
  return isStandardStep(type) ? t(STEP_LABEL_KEYS[type]) : type;
}

export function getChannelLabel(channel: MessageChannel, t: TFn): string {
  return t(CHANNEL_LABEL_KEYS[channel]);
}

export function getStepIcon(type: string): ComponentType<{ size?: number }> {
  return STEP_ICONS[type as StandardSequenceStep] ?? IconClock;
}

const S = StandardSequenceStep;

export const PLACEHOLDER_SEQUENCES: Record<MessageChannel, MessageSequenceStepData[]> = {
  [MessageChannel.LINKEDIN]: [
    {
      id: 1,
      delay: 1,
      delayUnit: MessageDelayUnit.DAY,
      type: S.OPENING,
      variants: [
        {
          id: 1,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}},\nJe suis tombé sur {{projet}} et j'aime beaucoup la direction que tu prends. On aide les fondateurs tech à structurer leur acquisition.\nIntéressé par un échange de 15 min cette semaine ?",
        },
      ],
    },
    {
      id: 2,
      delay: 4,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_1,
      variants: [
        {
          id: 2,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Salut {{prenom}}, je me permets de te relancer ici. J'accompagne des fondateurs comme toi sur l'acquisition B2B. Si tu veux, je peux t'envoyer un court audit personnalisé de {{projet}}.",
        },
      ],
    },
    {
      id: 3,
      delay: 8,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_2,
      variants: [
        {
          id: 3,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Hello {{prenom}}, dernier message de ma part. Si l'acquisition n'est pas la priorité pour le moment, dis-moi quand ce sera le cas, je serai ravi d'échanger.",
        },
      ],
    },
    {
      id: 4,
      delay: 14,
      delayUnit: MessageDelayUnit.DAY,
      type: S.BREAK_UP,
      variants: [
        {
          id: 4,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}}, je boucle ma séquence ici. Si jamais tu veux un retour rapide sur ta stratégie d'acquisition, je reste dispo.",
        },
      ],
    },
  ],
  [MessageChannel.EMAIL]: [
    {
      id: 1,
      delay: 1,
      delayUnit: MessageDelayUnit.DAY,
      type: S.OPENING,
      variants: [
        {
          id: 5,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}},\nJ'ai vu que {{entreprise}} recrute sur la partie commerciale. En général, c'est le moment où le discours se dilue entre les commerciaux.\nÇa vaut 15 min d'échange ?",
        },
      ],
    },
    {
      id: 2,
      delay: 3,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_1,
      variants: [
        {
          id: 6,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}}, je remonte mon message au cas où il serait passé au mauvais moment. Je peux t'envoyer un exemple concret de ce qu'on fait pour une équipe de la taille de {{entreprise}}.",
        },
      ],
    },
    {
      id: 3,
      delay: 7,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_2,
      variants: [
        {
          id: 7,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}}, une dernière idée avant de te laisser tranquille : on peut partir d'un seul appel de ton équipe et te montrer ce qui en ressort. Zéro engagement.",
        },
      ],
    },
    {
      id: 4,
      delay: 12,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_3,
      variants: [
        {
          id: 8,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}}, je me permets une dernière relance. Si le sujet est reporté à plus tard, dis-moi simplement quand je peux revenir vers toi.",
        },
      ],
    },
    {
      id: 5,
      delay: 20,
      delayUnit: MessageDelayUnit.DAY,
      type: S.BREAK_UP,
      variants: [
        {
          id: 9,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}}, je clôture le sujet de mon côté. Si l'analyse de vos appels redevient une priorité, ma porte reste ouverte.",
        },
      ],
    },
  ],
  [MessageChannel.WHATSAPP]: [
    {
      id: 1,
      delay: 1,
      delayUnit: MessageDelayUnit.DAY,
      type: S.OPENING,
      variants: [
        {
          id: 10,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Bonjour {{prenom}},\nOn a échangé sur {{projet}}, je me permets de continuer ici, c'est plus simple.\nTu es dispo cette semaine pour 15 min ?",
        },
      ],
    },
    {
      id: 2,
      delay: 2,
      delayUnit: MessageDelayUnit.DAY,
      type: S.FOLLOW_UP_1,
      variants: [
        {
          id: 11,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Salut {{prenom}}, je te relance rapidement. Un créneau demain ou jeudi te va ?",
        },
      ],
    },
    {
      id: 3,
      delay: 5,
      delayUnit: MessageDelayUnit.DAY,
      type: S.BREAK_UP,
      variants: [
        {
          id: 12,
          label: null,
          aiRules: null,
          position: 0,
          template:
            "Hello {{prenom}}, je n'insiste pas plus. Dis-moi juste si le sujet revient sur la table.",
        },
      ],
    },
  ],
};

export function availableStandardSteps(steps: MessageSequenceStepData[]): StandardSequenceStep[] {
  const used = new Set(steps.map((step) => step.type));
  return Object.values(StandardSequenceStep).filter((type) => !used.has(type));
}

export function standardStepSuggestions(
  otherSteps: MessageSequenceStepData[],
  t: TFn,
): SuggestionOption[] {
  const takenBy = new Map(otherSteps.map((step) => [step.type, step]));

  return Object.values(StandardSequenceStep).map((type) => {
    const taken = takenBy.get(type);
    if (!taken) {
      return { label: t(STEP_LABEL_KEYS[type]) };
    }
    return {
      label: t(STEP_LABEL_KEYS[type]),
      disabled: true,
      hint: String(t(TKey.MESSAGES_CONTENT_STEP_ALREADY_USED)).replace(
        "{{delay}}",
        getDelayLabel(taken.delay, taken.delayUnit, t),
      ),
    };
  });
}

const DEFAULT_DAY_GAP = 3;

export function suggestedNextDelay(steps: MessageSequenceStepData[]): number {
  if (steps.length === 0) {
    return 1;
  }
  const lastDay = Math.max(
    ...steps.map((step) => UMessageDelay.toDays(step.delay, step.delayUnit)),
  );
  return lastDay + DEFAULT_DAY_GAP;
}

export function resolveStepType(input: string, t: TFn): string {
  const trimmed = input.trim();
  const match = Object.values(StandardSequenceStep).find(
    (type) => t(STEP_LABEL_KEYS[type]) === trimmed,
  );
  return match ?? trimmed;
}

export function splitAvatarUpdatedMessage(text: string): [string, string] {
  const [before, after = ""] = text.split("{{ago}}");
  return [before, after];
}

export function variantLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

export function variantName(variant: { label: string | null }, index: number, t: TFn): string {
  const fallback = String(t(TKey.MESSAGES_CONTENT_VARIANT)).replace(
    "{{letter}}",
    variantLetter(index),
  );
  return variant.label?.trim() ? `${fallback} · ${variant.label}` : fallback;
}

export function resolveCurrentVariant(
  variants: MessageSequenceStepVariantData[],
  selectedId: number | null,
): MessageSequenceStepVariantData | undefined {
  return variants.find((variant) => variant.id === selectedId) ?? variants[0];
}

export function getDelayLabel(delay: number, unit: MessageDelayUnit, t: TFn): string {
  return String(t(DELAY_LABEL_KEYS[unit])).replace("{{n}}", String(delay));
}

export function variantsCountOptions(t: TFn): [string, string][] {
  return Array.from({ length: Limits.MAX_SEQUENCE_STEP_VARIANTS }, (_, index) => {
    const count = index + 1;
    const label = String(
      t(count === 1 ? TKey.MESSAGES_CONTENT_VARIANT_ONE : TKey.MESSAGES_CONTENT_VARIANT_MANY),
    ).replace("{{n}}", String(count));
    return [String(count), label];
  });
}

export function delayUnitOptions(t: TFn): [string, string][] {
  return [
    [MessageDelayUnit.DAY, String(t(TKey.MESSAGES_CONTENT_DELAY_UNIT_DAY))],
    [MessageDelayUnit.HOUR, String(t(TKey.MESSAGES_CONTENT_DELAY_UNIT_HOUR))],
    [MessageDelayUnit.MINUTE, String(t(TKey.MESSAGES_CONTENT_DELAY_UNIT_MINUTE))],
  ];
}
